import { FC, PropsWithChildren, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useTypedTranslationsWithPartialResult } from '../../../translation/use-translations';
import { textKeyIsSet } from '../utils/sportsbook-route-utils';

const getSeoTextKeys = (
	titleTextKey: string,
	descriptionTextKey: string,
	fallbackTitlePrefixTextKey?: string,
	fallbackTitleSuffixTextKey?: string,
	fallbackDescriptionPrefixTextKey?: string,
	fallbackDescriptionSuffixTextKey?: string
) => {
	const seoTextKeys: SeoTextKeys = {
		siteTitle: titleTextKey,
		metaDescription: descriptionTextKey
	};

	if (fallbackTitlePrefixTextKey) {
		seoTextKeys.fallbackTitlePrefix = fallbackTitlePrefixTextKey;
	}
	if (fallbackTitleSuffixTextKey) {
		seoTextKeys.fallbackTitleSuffix = fallbackTitleSuffixTextKey;
	}
	if (fallbackDescriptionPrefixTextKey) {
		seoTextKeys.fallbackDescriptionPrefix = fallbackDescriptionPrefixTextKey;
	}
	if (fallbackDescriptionSuffixTextKey) {
		seoTextKeys.fallbackDescriptionSuffix = fallbackDescriptionSuffixTextKey;
	}

	return seoTextKeys;
};

type SeoTextKeys = {
	siteTitle: string;
	metaDescription: string;
	fallbackTitlePrefix?: string;
	fallbackTitleSuffix?: string;
	fallbackDescriptionPrefix?: string;
	fallbackDescriptionSuffix?: string;
};

type SportsbookSeoHeaderProps = PropsWithChildren<{
	titleTextKey: string;
	descriptionTextKey: string;

	fallbackTitlePrefixTextKey?: string;
	fallbackTitleSuffixTextKey?: string;
	fallbackTitle?: (titlePrefix: string, titleSuffix: string) => string;
	fallbackDescriptionPrefixTextKey?: string;
	fallbackDescriptionSuffixTextKey?: string;
	fallbackDescription?: (descriptionPrefix: string, descriptionSuffix: string) => string;
}>;

const SportsbookSeoLobbyHeader: FC<SportsbookSeoHeaderProps> = ({
	titleTextKey,
	descriptionTextKey,
	fallbackTitlePrefixTextKey,
	fallbackTitleSuffixTextKey,
	fallbackTitle,
	fallbackDescriptionPrefixTextKey,
	fallbackDescriptionSuffixTextKey,
	fallbackDescription,
	children
}) => {
	const seoTextKeys = getSeoTextKeys(
		titleTextKey,
		descriptionTextKey,
		fallbackTitlePrefixTextKey,
		fallbackTitleSuffixTextKey,
		fallbackDescriptionPrefixTextKey,
		fallbackDescriptionSuffixTextKey
	);

	const typedTranslations = useTypedTranslationsWithPartialResult(seoTextKeys);

	const siteTitle = useMemo(() => {
		const translatedSiteTitle = typedTranslations.translations[seoTextKeys.siteTitle];

		// Sets default empty is fallback was not passed as a prop
		const fallbackTitlePrefix = seoTextKeys.fallbackTitlePrefix
			? typedTranslations.translations[seoTextKeys.fallbackTitlePrefix]
			: '';

		// Sets default empty is fallback was not passed as a prop
		const fallbackTitleSuffix = seoTextKeys.fallbackTitleSuffix
			? typedTranslations.translations[seoTextKeys.fallbackTitleSuffix]
			: '';

		// If we're still loading fallback translations, try showing translated site title
		if (fallbackTitlePrefix === undefined || fallbackTitleSuffix === undefined) {
			return textKeyIsSet(translatedSiteTitle) ? translatedSiteTitle : undefined;
		}

		// Show if fallbackTitle is set in prop or while loading the specific translation
		const shouldShowFallbackTitle =
			fallbackTitle && (typedTranslations.isLoadingTranslations || !textKeyIsSet(translatedSiteTitle));

		return shouldShowFallbackTitle ? fallbackTitle(fallbackTitlePrefix, fallbackTitleSuffix) : translatedSiteTitle;
	}, [
		fallbackTitle,
		seoTextKeys.fallbackTitlePrefix,
		seoTextKeys.fallbackTitleSuffix,
		seoTextKeys.siteTitle,
		typedTranslations.isLoadingTranslations,
		typedTranslations.translations
	]);

	const description = useMemo(() => {
		const translatedMetaDescription = typedTranslations.translations[seoTextKeys.metaDescription];

		// Sets default empty is fallback was not passed as a prop
		const fallbackDescriptionPrefix = seoTextKeys.fallbackDescriptionPrefix
			? typedTranslations.translations[seoTextKeys.fallbackDescriptionPrefix]
			: '';

		// Sets default empty is fallback was not passed as a prop
		const fallbackDescriptionSuffix = seoTextKeys.fallbackDescriptionSuffix
			? typedTranslations.translations[seoTextKeys.fallbackDescriptionSuffix]
			: '';

		// If we're still loading fallback translations, try showing translated description
		if (fallbackDescriptionPrefix === undefined || fallbackDescriptionSuffix === undefined) {
			return textKeyIsSet(translatedMetaDescription) ? translatedMetaDescription : undefined;
		}

		const shouldShowFallbackDescription =
			fallbackDescription &&
			(typedTranslations.isLoadingTranslations || !textKeyIsSet(translatedMetaDescription));

		return shouldShowFallbackDescription
			? fallbackDescription(fallbackDescriptionPrefix, fallbackDescriptionSuffix)
			: translatedMetaDescription;
	}, [
		typedTranslations.translations,
		typedTranslations.isLoadingTranslations,
		seoTextKeys.metaDescription,
		seoTextKeys.fallbackDescriptionPrefix,
		seoTextKeys.fallbackDescriptionSuffix,
		fallbackDescription
	]);

	return (
		<Helmet>
			{siteTitle && <title>{siteTitle}</title>}
			{description && <meta name="description" content={description} />}
			{children}
		</Helmet>
	);
};

export default SportsbookSeoLobbyHeader;
